/**
 * Log redaction — scrub obvious secrets/PII out of log messages and structured
 * data BEFORE they reach the console OR the durable sink (log_events). This is
 * the last line before egress, so it runs on every log line and is hot-path
 * constrained:
 *   - depth-capped object recursion (MAX_DEPTH),
 *   - a scan cap on long strings (MAX_SCAN chars),
 *   - zero allocation on the common no-secret line: every function returns the
 *     SAME reference it was handed when nothing matched (a gate `.test()` for
 *     strings, lazy clone-on-first-change for objects/arrays).
 *
 * Gated by LOG_REDACTION_V1: only the literal string 'false' disables it — unset
 * or any other value keeps it ON. When OFF, the entry points return their input
 * unchanged (same reference), so logger output is byte-identical to pre-redaction.
 */

const REDACTED = '[redacted]';

/** Max object/array nesting we recurse into. Deeper values pass through as-is. */
const MAX_DEPTH = 4;

/** Only the first MAX_SCAN chars of any string are scanned for secret patterns. */
const MAX_SCAN = 10_000;

/**
 * Names that mark a value as a credential wherever it appears, in a quoted JSON
 * pair or a bare `key=value`. The same vocabulary SECRET_KEY_FRAGMENTS uses for
 * object keys, written as an alternation so a free-text message gets the rule a
 * structured payload already had.
 */
const SECRET_NAMES = 'api[_-]?key|token|secret|password|passwd|client[_-]?secret';

/**
 * The VALUE half of a pair: a quoted run, or an unquoted run that stops short of
 * two things. `\S+` alone would swallow the full stop after a token and glue the
 * next sentence onto the redacted one, so the LAST character is never sentence
 * punctuation. And a run that crossed a JSON delimiter would eat the rest of a
 * SERIALISED payload: a pair written inside one value (`{"note":"token=abc",…}`)
 * took the closing quote, the comma and every field after it, leaving text that
 * still parsed as JSON with one key (R-2a-34). So no character of the value is a
 * quote, a comma, a closing bracket or a closing brace either.
 */
const VALUE_BODY = `[^\\s"',\\]}]`;
/**
 * The QUOTED alternatives take the same stop. `token=` with an EMPTY value fell
 * through to a bare `"[^"]*"`, which inside serialised JSON matched the closing
 * quote of the enclosing string, the comma and the next opening quote: the field
 * NAME after it was eaten and the line no longer parsed. A genuine quoted value
 * holds no JSON delimiter, so excluding them costs nothing and closes that.
 */
const SECRET_VALUE = `"[^"',\\]}]*"|'[^"',\\]}]*'|${VALUE_BODY}*[^\\s"',\\]}.;:!?]`;

/**
 * The schemes an `authorization` value may carry BEFORE its credential. Named
 * literally rather than "any word": the optional group used to take whatever
 * word came first, so an honest sentence ("authorization: pending review for
 * the invoice") lost a second word to the redaction. `Bearer` is excluded by a
 * lookahead and handled by its own rule, which keeps the scheme word.
 */
const AUTH_SCHEMES = 'Basic|Digest|Negotiate|NTLM|OAuth|Token|ApiKey|Hawk|AWS4-HMAC-SHA256';

/**
 * String-value secret patterns. Order matters: a JWT inside a `Bearer <jwt>`
 * is caught by the JWT rule first, leaving `Bearer [redacted]` for the Bearer rule.
 * The two NAMED-PAIR rules run before every shaped-token rule, so a credential
 * gets caught by the name it was written under even when its own shape is one
 * nothing here would recognise.
 */
const PATTERNS: ReadonlyArray<{ re: RegExp; rep: string }> = [
  // A JSON-ish `"token": "…"` pair: the key and the quotes stay, the value goes.
  // First, so the quoted form never reaches the bare-pair rules below.
  {
    re: new RegExp(`"(${SECRET_NAMES}|authorization)"(\\s*:\\s*)"[^"]*"`, 'gi'),
    rep: `"$1"$2"${REDACTED}"`,
  },
  // `authorization: <scheme> <credential>` — its own rule because the value of an
  // authorization pair is commonly TWO words, and a rule that took one would leave
  // the credential standing beside a redacted scheme name. A `Bearer` value is left
  // to the Bearer rule below, which keeps the scheme word.
  {
    re: new RegExp(
      `\\b(authorization)(\\s*[=:]\\s*)(?!Bearer\\b)(?:(?:${AUTH_SCHEMES})\\s+)?(?:${SECRET_VALUE})`,
      'gi',
    ),
    rep: `$1$2${REDACTED}`,
  },
  // A bare `key=value` or `key: value` pair, the shape a vendor error, a curl
  // echo or a request line carries. The key and its separator stay so the line
  // still reads; the value goes whole, quoted or not.
  {
    re: new RegExp(`\\b(${SECRET_NAMES})(\\s*[=:]\\s*)(?!Bearer\\b)(?:${SECRET_VALUE})`, 'gi'),
    rep: `$1$2${REDACTED}`,
  },
  // OpenAI-style keys: sk-... (also sk-proj-..., sk-ant-...).
  { re: /sk-[A-Za-z0-9_-]{16,}/g, rep: REDACTED },
  // Slack bot, user, app and legacy tokens: xoxb-, xoxp-, xoxa-, xoxo-, xoxs-, xoxr-.
  { re: /xox[abopsr]-[A-Za-z0-9-]+/gi, rep: REDACTED },
  // JWT triplet (header.payload.signature), incl. bare `eyJ...` tokens.
  { re: /eyJ[A-Za-z0-9_-]{6,}\.[A-Za-z0-9_-]{6,}\.[A-Za-z0-9_-]{4,}/g, rep: REDACTED },
  // `Bearer <token>` — keep the scheme word, drop the credential.
  { re: /Bearer\s+[A-Za-z0-9._~+/=-]+/gi, rep: `Bearer ${REDACTED}` },
  // AWS access key id.
  { re: /AKIA[0-9A-Z]{16}/g, rep: REDACTED },
  // Postgres connection strings carrying an inline password.
  { re: /postgres(?:ql)?:\/\/[^\s:@/]+:[^\s@/]+@\S+/gi, rep: REDACTED },
  // Slack incoming-webhook URLs (the path segment is the credential).
  { re: /https:\/\/hooks\.slack\.com\/\S+/gi, rep: REDACTED },
];

/** Single non-global alternation used as a cheap presence gate before replacing. */
const GATE = new RegExp(PATTERNS.map((p) => p.re.source).join('|'), 'i');

/**
 * Object keys whose VALUE is a secret regardless of shape. Matched by normalized
 * substring (lowercased, separators stripped) so `accessToken`, `api_key`,
 * `x-api-key`, `set-cookie`, `refresh_token`, `webhookUrl` all hit.
 */
const SECRET_KEY_FRAGMENTS = [
  'token',
  'secret',
  'apikey',
  'password',
  'passwd',
  'authorization',
  'bearer',
  'cookie',
  'dsn',
  'webhook',
];

function isSecretKey(key: string): boolean {
  const norm = key.toLowerCase().replace(/[_\-\s]/g, '');
  for (const frag of SECRET_KEY_FRAGMENTS) {
    if (norm.includes(frag)) return true;
  }
  return false;
}

/** Scrub a single string. Returns the same reference when nothing matched. */
function scrubString(value: string): string {
  if (value.length === 0) return value;
  // Only scan the first MAX_SCAN chars; anything past the cap is left untouched.
  const head = value.length > MAX_SCAN ? value.slice(0, MAX_SCAN) : value;
  if (!GATE.test(head)) return value;
  let scrubbed = head;
  for (const { re, rep } of PATTERNS) scrubbed = scrubbed.replace(re, rep);
  return value.length > MAX_SCAN ? scrubbed + value.slice(MAX_SCAN) : scrubbed;
}

/**
 * Recursively scrub a value. Strings are always scrubbed (even leaf strings past
 * the depth cap); object/array recursion stops at MAX_DEPTH. Allocates a clone
 * only on the first changed member, so an untouched value returns by reference.
 */
function scrubValue(value: unknown, depth: number): unknown {
  if (typeof value === 'string') return scrubString(value);
  if (value === null || typeof value !== 'object') return value;
  if (depth >= MAX_DEPTH) return value;

  if (Array.isArray(value)) {
    let out: unknown[] | undefined;
    for (let i = 0; i < value.length; i++) {
      const next = scrubValue(value[i], depth + 1);
      if (next !== value[i]) {
        if (!out) out = value.slice();
        out[i] = next;
      }
    }
    return out ?? value;
  }

  let out: Record<string, unknown> | undefined;
  const obj = value as Record<string, unknown>;
  for (const key of Object.keys(obj)) {
    const current = obj[key];
    const next = isSecretKey(key) ? REDACTED : scrubValue(current, depth + 1);
    if (next !== current) {
      if (!out) out = { ...obj };
      out[key] = next;
    }
  }
  return out ?? value;
}

/** True unless LOG_REDACTION_V1 is explicitly 'false'. Read per-call so tests and runtime toggles take effect. */
export function isLogRedactionEnabled(): boolean {
  return process.env.LOG_REDACTION_V1 !== 'false';
}

/**
 * The rule set with NO flag read (R-2a-33, I4): what a string SERVED to a person
 * or a model takes. `LOG_REDACTION_V1` exists to make LOG bytes reproducible,
 * and a composer card's rendered vendor error and vendor payload are not logs,
 * so an operator debugging log output must not be able to put a credential into
 * a reply. Same reference when nothing matched.
 */
export function redactSecrets(text: string): string {
  return scrubString(text);
}

/** Scrub a log message string. Same reference when disabled or nothing matched. */
export function redactMessage(message: string): string {
  if (!isLogRedactionEnabled()) return message;
  return redactSecrets(message);
}

/** Scrub a structured-data object. Same reference when disabled or nothing matched. */
export function redactData(
  data: Record<string, unknown> | undefined,
): Record<string, unknown> | undefined {
  if (data === undefined || !isLogRedactionEnabled()) return data;
  return scrubValue(data, 0) as Record<string, unknown>;
}
